/**
 * Shared styled components and sub-components used across the
 * rule diff preview and apply modals.
 */
import React, { useMemo, useState } from 'react';
import ReactJson from 'react-json-view';
import styled from 'styled-components';

/* ── Environment helpers ── */

/**
 * Canonical backend environment keys — exactly the values the rules API
 * accepts as `source` and reports as the active env — mapped to display
 * labels. 'truist-prod' is spelled out (rather than 'prod') because of a
 * backend naming conflict that could only be resolved with this value.
 *
 * This is the ONE naming convention used everywhere the migrate/diff UI talks
 * about environments, so comparisons (e.g. excluding the current env from the
 * source list) and labels stay consistent.
 */
export const ENV_LABELS = {
  atd: 'ATD',
  'tp-uat1': 'UAT1',
  'tp-uat2': 'UAT2',
  'truist-prod': 'Prod',
};

/**
 * Maps the build-time REACT_APP_ENV to its canonical backend env key (the keys
 * of ENV_LABELS / ACCESSIBLE_ENVIRONMENTS). Note REACT_APP_ENV uses 'tp-prod'
 * for prod whereas the backend key is 'truist-prod', so this mapping is not an
 * identity for every env.
 */
export const ENV_TO_BACKEND = {
  local: 'atd',
  atd: 'atd',
  'tp-uat1': 'tp-uat1',
  'tp-uat2': 'tp-uat2',
  'tp-prod': 'truist-prod',
};
/**
 * Governs which source/destination environments are selectable, keyed by
 * REACT_APP_ENV. Each value is a list of [backendEnvKey, label] pairs; the
 * backendEnvKey is sent verbatim to the API as `source`.
 */
export const ACCESSIBLE_ENVIRONMENTS = {
  local: [['atd', 'ATD'],['tp-uat1', 'UAT1'], ['tp-uat2', 'UAT2'], ['truist-prod', 'Prod']],
  atd: [['atd', 'ATD'],['tp-uat1', 'UAT1'], ['tp-uat2', 'UAT2'], ['truist-prod', 'Prod']],
  'tp-uat1': [['tp-uat1', 'UAT1'], ['tp-uat2', 'UAT2'], ['truist-prod', 'Prod']],
  'tp-uat2': [['tp-uat1', 'UAT1'], ['tp-uat2', 'UAT2'], ['truist-prod', 'Prod']],
  'tp-prod': [['tp-uat1', 'UAT1'], ['tp-uat2', 'UAT2'], ['truist-prod', 'Prod']],
}

/**
 * Returns the canonical backend env key for the running frontend — in the same
 * naming convention as the ACCESSIBLE_ENVIRONMENTS keys and the backend's
 * `source`/active-env values. Falls back to 'atd' for local/unknown envs.
 */
export const getCurrentBackendEnv = () =>
  ENV_TO_BACKEND[process.env.REACT_APP_ENV] || 'atd';

/**
 * Returns the list of accessible backend environments for the running frontend.
 * Falls back to the atd entry so local/unknown envs still render a list.
 */
export const getAccessibleEnvironments = () =>
  ACCESSIBLE_ENVIRONMENTS[process.env.REACT_APP_ENV] ||
  ACCESSIBLE_ENVIRONMENTS['atd'];

/**
 * Returns the human-readable label for a backend env key (e.g. 'truist-prod'
 * -> 'Prod'). Falls back to the upper-cased key for unknown values.
 */
export const getEnvLabel = envKey =>
  ENV_LABELS[envKey] || ((envKey ?? '').toUpperCase());

/* ── Styled components ── */

export const ModalContainer = styled.div`
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 10px;
  box-sizing: border-box;
`;

export const SummarySection = styled.div`
  margin-bottom: 24px;
`;

export const SummaryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 12px;

  th,
  td {
    text-align: left;
    padding: 8px 12px;
    border-bottom: 1px solid #e0e0e0;
  }

  th {
    font-weight: 600;
    color: #555;
    font-size: 13px;
    text-transform: uppercase;
  }
`;

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: white;
  background: ${props => {
    switch (props.status) {
      case 'added':
        return '#4caf50';
      case 'modified':
        return '#ff9800';
      case 'removed':
        return '#f44336';
      case 'unknown':
      default:
        return '#9e9e9e';
    }
  }};
`;

export const ChangedFields = styled.span`
  font-size: 12px;
  color: #777;
`;

export const FilterBar = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

export const FilterButton = styled.button`
  border: 1px solid #d0d0d0;
  background: ${props => (props.active ? '#333' : '#fff')};
  color: ${props => (props.active ? '#fff' : '#555')};
  padding: 4px 12px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-transform: capitalize;

  &:hover {
    border-color: #999;
  }
`;

/**
 * A clickable table header that shows the active sort direction.
 * Inherits the base <th> styling from SummaryTable.
 */
export const SortableHeader = styled.th`
  cursor: pointer;
  user-select: none;
  white-space: nowrap;

  &:hover {
    color: #000;
  }

  span {
    margin-left: 4px;
    font-size: 11px;
    color: #999;
  }
`;

export const ErrorItem = styled.li`
  background: #fff5f5;
  border-left: 4px solid #f44336;
  padding: 12px 16px;
  margin-bottom: 8px;
  border-radius: 0 4px 4px 0;
  list-style: none;
`;

export const ErrorRuleName = styled.div`
  font-weight: 600;
  color: #d32f2f;
  margin-bottom: 4px;
`;

export const ErrorDetail = styled.div`
  font-size: 13px;
  color: #555;
  margin-left: 8px;
  word-break: break-word;
`;

export const ErrorList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const ValidationBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff5f5;
  border: 1px solid #f5c6cb;
  border-left: 4px solid #f44336;
  color: #b71c1c;
  padding: 12px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  margin: 8px 0 16px 0;
`;

export const ErrorIndexBadge = styled.span`
  display: inline-block;
  background: #f44336;
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 8px;
  border-radius: 10px;
  margin-right: 8px;
  vertical-align: middle;
`;

export const SectionTitle = styled.h3`
  margin: 20px 0 8px 0;
  font-size: 16px;
  color: #333;
`;

export const RawDiffContainer = styled.div`
  padding: 10px 20px;
  max-height: 400px;
  overflow: auto;
  background: #fafafa;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  word-break: break-all;
  box-sizing: border-box;
  width: 100%;
`;

export const CloseButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 0 0 0;
`;

export const DirectionLabel = styled.div`
  font-size: 14px;
  color: #555;
  margin-bottom: 16px;

  strong {
    text-transform: uppercase;
  }
`;

/**
 * Extracts the validation-error list from a backend response.
 *
 * Every rules endpoint returns the unified envelope { status, details, diff },
 * where `details` is the error list on failure (and null on success). Each
 * entry is a per-rule object { rule_index, rule_name, errors[], input };
 * request-level problems use the same shape with rule_index/rule_name null.
 *
 * For resilience we also accept a legacy top-level `errors` array and a flat
 * array of strings under `details`; ValidationErrors renders any of these.
 * Returns the non-empty array if present, otherwise null.
 */
export const getValidationErrors = data => {
  if (!data) return null;
  if (Array.isArray(data.errors) && data.errors.length > 0) {
    return data.errors;
  }
  if (Array.isArray(data.details) && data.details.length > 0) {
    return data.details;
  }
  return null;
};

/* ── Shared sub-components ── */

/* The status filters offered above the summary table. `unknown` covers any
 * status the backend reports that isn't one of the expected ones, so those
 * rows stay visible and filterable instead of being dropped. */
const STATUS_FILTERS = ['all', 'added', 'modified', 'removed', 'unknown'];

/* Statuses the backend is expected to report for an updated rule. */
const KNOWN_DIFF_STATUSES = ['added', 'modified', 'removed'];

/* Map any unexpected/missing status to 'unknown' so the rule is still listed
 * (with a neutral badge) rather than dropped or shown with a raw value. */
const normalizeDiffStatus = status =>
  KNOWN_DIFF_STATUSES.includes(status) ? status : 'unknown';

/**
 * Renders a summary table of rule diffs: added / modified / removed.
 * Accepts the diff object containing updated_rules and removed_rules.
 *
 * Rows can be filtered by status (All / Added / Modified / Removed) and
 * sorted by Rule Name or Status by clicking the column headers.
 */
export function DiffSummary({ diff, emptyMessage }) {
  const { updated_rules = {}, removed_rules = [] } = diff || {};

  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState({ field: 'ruleName', direction: 'asc' });

  // Flatten updated_rules (object) and removed_rules (array) into one row list.
  const rows = useMemo(() => {
    const updatedRows = Object.entries(updated_rules).map(
      ([ruleName, detail]) => ({
        ruleName,
        status: normalizeDiffStatus(detail && detail.status),
        changedFields:
          detail && detail.changed_fields && detail.changed_fields.length
            ? detail.changed_fields.join(', ')
            : '—',
      })
    );
    const removedRows = removed_rules.map(ruleName => ({
      ruleName,
      status: 'removed',
      changedFields: '—',
    }));
    return [...updatedRows, ...removedRows];
  }, [updated_rules, removed_rules]);

  // Count rows per status to label the filter buttons.
  const counts = useMemo(() => {
    const tally = { all: rows.length, added: 0, modified: 0, removed: 0, unknown: 0 };
    rows.forEach(row => {
      if (tally[row.status] != null) tally[row.status] += 1;
    });
    return tally;
  }, [rows]);

  const visibleRows = useMemo(() => {
    const filtered =
      statusFilter === 'all'
        ? rows
        : rows.filter(row => row.status === statusFilter);

    const sorted = [...filtered].sort((a, b) => {
      const cmp = String(a[sort.field]).localeCompare(
        String(b[sort.field]),
        undefined,
        { sensitivity: 'base' }
      );
      return sort.direction === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [rows, statusFilter, sort]);

  const toggleSort = field => {
    setSort(prev =>
      prev.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'asc' }
    );
  };

  const sortIndicator = field =>
    sort.field === field ? (
      <span>{sort.direction === 'asc' ? '▲' : '▼'}</span>
    ) : null;

  const hasChanges = rows.length > 0;

  if (!hasChanges) {
    return (
      <SummarySection>
        <SectionTitle>Summary</SectionTitle>
        <p>{emptyMessage || 'No rule changes detected.'}</p>
      </SummarySection>
    );
  }

  return (
    <SummarySection>
      <SectionTitle>Summary</SectionTitle>
      <FilterBar>
        {STATUS_FILTERS.filter(
          // Keep `all`, and only show a change-type pill when at least one row
          // has that status — hide types with none present.
          status => status === 'all' || counts[status] > 0
        ).map(status => (
          <FilterButton
            key={status}
            type='button'
            active={statusFilter === status}
            onClick={() => setStatusFilter(status)}
          >
            {status} ({counts[status]})
          </FilterButton>
        ))}
      </FilterBar>
      <SummaryTable>
        <thead>
          <tr>
            <SortableHeader onClick={() => toggleSort('ruleName')}>
              Rule Name{sortIndicator('ruleName')}
            </SortableHeader>
            <SortableHeader onClick={() => toggleSort('status')}>
              Status{sortIndicator('status')}
            </SortableHeader>
            <th>Changed Fields</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(row => (
            <tr key={`${row.status}-${row.ruleName}`}>
              <td>{row.ruleName}</td>
              <td>
                <StatusBadge status={row.status}>{row.status}</StatusBadge>
              </td>
              <td>
                <ChangedFields>{row.changedFields}</ChangedFields>
              </td>
            </tr>
          ))}
        </tbody>
      </SummaryTable>
    </SummarySection>
  );
}

/**
 * Parses a backend validation string of the form
 *   Rule '<name>' (index <n>): <message>
 * into its parts. Returns null when the string doesn't match that shape,
 * so the caller can fall back to rendering it verbatim.
 */
const parseValidationError = entry => {
  if (typeof entry !== 'string') return null;
  const match = entry.match(/^Rule\s+'(.+)'\s+\(index\s+(\d+)\):\s*([\s\S]*)$/);
  if (!match) return null;
  return { ruleName: match[1], index: match[2], message: match[3].trim() };
};

/**
 * Renders validation errors returned by the backend.
 *
 * The backend's `validation_failed` / `error` response carries `details`: an
 * array of per-rule objects:
 *   { rule_index, rule_name, errors: [<message>, ...], input: {<rule>} }
 * Each entry is shown as a card: the rule name with its position badge
 * ("index <n>") and one bullet per message. Request-level problems (empty
 * body, bad source env, etc.) use the same shape with rule_index/rule_name
 * null and render as a plain message with no rule header.
 *
 * For resilience we also accept a flat array of strings (a legacy shape).
 * Strings matching "Rule '<name>' (index <n>): <message>" are parsed into the
 * same card layout; any other string is shown verbatim.
 */
export function ValidationErrors({ errors }) {
  const count = errors.length;

  return (
    <SummarySection>
      <SectionTitle>Validation Errors</SectionTitle>
      <ValidationBanner>
        ⚠ {count} issue{count === 1 ? '' : 's'} found — fix the rule
        {count === 1 ? '' : 's'} below before applying these rules.
      </ValidationBanner>
      <ErrorList>
        {errors.map((entry, idx) => {
          // String shape: parse "Rule '<name>' (index <n>): <message>" when
          // possible, otherwise render the string verbatim.
          if (typeof entry === 'string') {
            const parsed = parseValidationError(entry);
            if (parsed) {
              return (
                <ErrorItem key={idx}>
                  <ErrorRuleName>
                    <ErrorIndexBadge>index {parsed.index}</ErrorIndexBadge>
                    {parsed.ruleName}
                  </ErrorRuleName>
                  <ErrorDetail>{parsed.message}</ErrorDetail>
                </ErrorItem>
              );
            }
            return (
              <ErrorItem key={idx}>
                <ErrorDetail>{entry}</ErrorDetail>
              </ErrorItem>
            );
          }

          // Object shape: { rule_index, rule_name, errors: [...] }. A
          // request-level error (empty body, bad source env, etc.) has no
          // rule_index/rule_name — render its message(s) without a rule header.
          const messages = Array.isArray(entry.errors) ? entry.errors : [];
          const hasRuleHeader = entry.rule_index != null || !!entry.rule_name;
          return (
            <ErrorItem key={idx}>
              {hasRuleHeader && (
                <ErrorRuleName>
                  {entry.rule_index != null && (
                    <ErrorIndexBadge>index {entry.rule_index}</ErrorIndexBadge>
                  )}
                  {entry.rule_name || `Rule #${idx}`}
                </ErrorRuleName>
              )}
              {messages.map((msg, i) => (
                <ErrorDetail key={i}>
                  {hasRuleHeader ? `• ${msg}` : msg}
                </ErrorDetail>
              ))}
            </ErrorItem>
          );
        })}
      </ErrorList>
    </SummarySection>
  );
}

/**
 * Renders the raw JSON response inside a scrollable, bordered container.
 */
export function RawJsonView({ src }) {
  return (
    <>
      <SectionTitle>Raw Response</SectionTitle>
      <RawDiffContainer>
        <ReactJson
          src={src}
          collapsed={2}
          displayDataTypes={false}
          name={false}
          enableClipboard
        />
      </RawDiffContainer>
    </>
  );
}
